import { FC, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"

import { InfoDropdownList, InfoDropdownListItemButton } from "components/InfoDropdown"
import Tooltip from "components/Tooltip/Tooltip"
import useOutgoingInvoice from "hooks/useOutgoingInvoice"
import { Guid, IApiLink } from "@/types"
import Spinner from "components/Spinner/Spinner"
import { PartialNoteType } from "components/PartialNoteCreateModal/PartialNoteCreateModal"
import { useTranslation, Trans } from "react-i18next"
import { OutgoingInvoice } from "models/OutgoingInvoice"
import Routes from "routes/index"
import confirmModals from "stores/confirmModals"
import AssetService from "services/AssetService"
import toastMessages from "stores/toastMessages"
import CustomError from "models/CustomError"
import { ErrorCode } from "config/constants"

interface IOutgoingInvoiceControlsDropdownMenuProps {
    reservationId: Guid
    invoiceId: Guid
    hidePartialNotes?: boolean
    resubmitInvoiceLink?: IApiLink
    isInvoicePrinted?: boolean
    close(): void
    onPartialNoteCreateClick(type: PartialNoteType): void
    onInvoiceFetched?(outgoingInvoice: OutgoingInvoice): void
    onInvoiceResubmitted?(invoiceId: Guid): void
    onEditOrderNumberClick(link: IApiLink, currentOrderNumber?: string): void
    onInvoiceChanged?(): void | Promise<void>
}

const OutgoingInvoiceControlsDropdownMenu: FC<IOutgoingInvoiceControlsDropdownMenuProps> = ({
    reservationId,
    invoiceId,
    hidePartialNotes,
    resubmitInvoiceLink,
    isInvoicePrinted,
    close,
    onPartialNoteCreateClick,
    onInvoiceFetched,
    onInvoiceResubmitted,
    onEditOrderNumberClick,
    onInvoiceChanged,
}) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const { invoice } = useOutgoingInvoice({ invoiceId })

    useEffect(() => {
        if (!onInvoiceFetched || !invoice) return
        onInvoiceFetched(invoice)
    }, [onInvoiceFetched, invoice])

    function renderDropdownItem({
        disabled,
        disabledTooltip,
        onClick,
        label,
    }: {
        disabled: boolean
        disabledTooltip: string
        onClick?(): void
        label: string
    }) {
        const button = <InfoDropdownListItemButton
            disabled={disabled}
            onClick={() => {
                close()
                onClick?.()
            }}>
            {label}
        </InfoDropdownListItemButton>

        if (disabled) {
            return (
                <Tooltip
                    content={disabledTooltip}
                    trigger="hover"
                    placement="left">
                    {button}
                </Tooltip>
            )
        }

        return button
    }

    function openDeleteInvoiceModal() {
        if (!invoice?.linkDeleteInvoice) return
        const link = invoice.linkDeleteInvoice
        confirmModals.show({
            title: t("outgoingInvoiceControls.deleteInvoiceTitle"),
            children: (
                <div>
                    <p>{t("outgoingInvoiceControls.deleteInvoiceWarningHeader")}</p>
                    <ul className="pl-4">
                        <li className="mb-1"><Trans i18nKey="outgoingInvoiceControls.deleteInvoiceBullet1" /></li>
                        <li className="mb-1"><Trans i18nKey="outgoingInvoiceControls.deleteInvoiceBullet2" /></li>
                    </ul>
                </div>
            ),
            confirmButtonLabel: t("common.delete"),
            theme: "danger",
            maxWidth: "586px",
            onConfirm: async confirmClose => {
                try {
                    await AssetService.deleteOutgoingInvoice(link)
                    confirmClose?.()
                    await onInvoiceChanged?.()
                } catch (e) {
                    if (e instanceof CustomError && e.code === ErrorCode.conflict) {
                        toastMessages.show({
                            type: "error",
                            title: t("outgoingInvoiceControls.deleteInvoiceConflict"),
                        })
                    } else {
                        toastMessages.showSystemError()
                    }
                }
            }
        })
    }

    async function resubmitInvoiceModal() {
        confirmModals.show({
            title: t("common.warning"),
            children:
                <div>
                    <p>{t("outgoingInvoiceControls.resubmitInvoiceModalTextHeader")}</p>
                    <ul className="pl-4">
                        <li className="mb-1"><Trans i18nKey="outgoingInvoiceControls.resubmitBullet1" /></li>
                        <li className="mb-1"><Trans i18nKey="outgoingInvoiceControls.resubmitBullet2" /></li>
                        <li className="mb-1"><Trans i18nKey="outgoingInvoiceControls.resubmitBullet3" /></li>
                    </ul>
                </div>
            ,
            confirmButtonLabel: t("common.confirm"),
            theme: "danger",
            maxWidth: "586px",
            onConfirm: async confirmClose => {
                confirmClose?.()
                try {
                    await AssetService.resubmitInvoice(resubmitInvoiceLink!)
                    if (onInvoiceResubmitted) {
                        onInvoiceResubmitted(invoiceId);
                    }
                } catch (e) {
                    toastMessages.showSystemError()
                }
            }
        })
    }

    if (!invoice) {
        return <Spinner size={32} className="px-10 py-4" />
    }

    return (
        <InfoDropdownList>
            <InfoDropdownListItemButton onClick={() => {
                close()
                navigate(Routes.outgoingInvoiceView.getPath({ invoiceId }), { state: { backUrl: location.pathname + location.search, reopenReservationSidebarId: reservationId } })
            }}>
                {t('outgoingInvoiceControls.viewInvoice')}
            </InfoDropdownListItemButton>

            {isInvoicePrinted && (
                <>
                    {renderDropdownItem({
                        disabled: !invoice.linkCancelInvoice,
                        disabledTooltip: t('outgoingInvoiceControls.cancelInvoiceDisabledTooltip'),
                        onClick: () => window.open(Routes.cancelInvoices.getPath({}, { reservationId, invoiceIds: [invoiceId] }), "_blank"),
                        label: t('outgoingInvoiceControls.cancelInvoice'),
                    })}

                    {!hidePartialNotes && (
                        <>
                            {renderDropdownItem({
                                disabled: !invoice.linkAddCreditNote,
                                disabledTooltip: t('outgoingInvoiceControls.createCreditNoteDisabledTooltip'),
                                onClick: () => onPartialNoteCreateClick(PartialNoteType.credit),
                                label: t('outgoingInvoiceControls.createCreditNote'),
                            })}
                            {renderDropdownItem({
                                disabled: !invoice.linkAddDebitNote,
                                disabledTooltip: t('outgoingInvoiceControls.createDebitNoteDisabledTooltip'),
                                onClick: () => onPartialNoteCreateClick(PartialNoteType.debit),
                                label: t('outgoingInvoiceControls.createDebitNote'),
                            })}
                        </>
                    )}
                </>
            )}

            {!isInvoicePrinted &&
                renderDropdownItem({
                    disabled: !resubmitInvoiceLink,
                    disabledTooltip: t('outgoingInvoiceControls.resubmitInvoiceTooltip'),
                    onClick: () => resubmitInvoiceModal(),
                    label: t('outgoingInvoiceControls.resubmitInvoice'),
                })}

            {!isInvoicePrinted &&
                renderDropdownItem({
                    disabled: !invoice.linkEditOrderNumber,
                    disabledTooltip: t('outgoingInvoiceControls.editOrderNumberDisabledTooltip'),
                    onClick: () => invoice.linkEditOrderNumber && onEditOrderNumberClick(invoice.linkEditOrderNumber, invoice.erpOrderNumber),
                    label: t('outgoingInvoiceControls.editOrderNumber'),
                })}

            {!isInvoicePrinted &&
                renderDropdownItem({
                    disabled: !invoice.linkDeleteInvoice,
                    disabledTooltip: t('outgoingInvoiceControls.deleteInvoiceDisabledTooltip'),
                    onClick: openDeleteInvoiceModal,
                    label: t('outgoingInvoiceControls.deleteInvoice'),
                })}
        </InfoDropdownList>
    )
}

export default OutgoingInvoiceControlsDropdownMenu
